import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import {
  CurrencyDollar, TrendUp, Users, GlobeHemisphereEast, Broadcast, ShieldWarning,
  ChartLineUp, Path, BellRinging, Plus, PaperPlaneTilt, CheckCircle, Warning,
  MagnifyingGlass, Rocket,
} from "@phosphor-icons/react";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip, Cell } from "recharts";

const Metric = ({ label, value, sub, accent }) => (
  <div className="rounded-md border border-white/10 bg-card p-4">
    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
    <p className={`mt-2 font-mono-metric text-2xl font-semibold ${accent || ""}`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <section className="space-y-3">
    <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
      <Icon size={15} weight="bold" className="text-primary" /> {title}
    </h2>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
  </section>
);

function QuickCredit() {
  const [id, setId] = useState(""); const [amt, setAmt] = useState(""); const [busy, setBusy] = useState(false);
  const credit = async () => {
    const cents = Math.round(Number(amt) * 100);
    if (!id.trim() || !cents) { toast.error("Enter a customer and amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/credit-customer", { identifier: id.trim(), amount_cents: cents, note: "WhatsApp receipt" });
      toast.success(`Credited ${data.name}: $${(data.amount_cents / 100).toFixed(2)} · new balance $${(data.balance_cents / 100).toFixed(2)}`);
      setId(""); setAmt("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not credit"); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-lg border border-primary/30 bg-primary/[0.05] p-4" data-testid="quick-credit">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary"><Plus size={15} weight="bold" /> Credit a customer · WhatsApp receipt</h2>
        <Link to="/command/messaging" data-testid="go-messaging" className="flex items-center gap-1.5 rounded-full bg-card px-3 py-1.5 text-xs font-semibold hover:text-primary"><PaperPlaneTilt size={13} weight="bold" /> Messaging</Link>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input data-testid="credit-identifier" value={id} onChange={(e) => setId(e.target.value)} placeholder="customer email or phone" className="min-w-0 flex-1 rounded-xl border border-white/10 bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
        <input data-testid="credit-amount" type="number" min="0" step="0.5" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="$ amount" className="rounded-xl border border-white/10 bg-background px-3 py-2.5 text-sm outline-none focus:border-primary sm:w-32" />
        <button data-testid="credit-submit" onClick={credit} disabled={busy} className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Crediting…" : "Credit now"}</button>
      </div>
    </section>
  );
}

function NumberHealth() {
  const { data } = useQuery({ queryKey: ["admin-number-health"], queryFn: async () => (await api.get("/admin/number-health")).data, refetchInterval: 60000 });
  const nums = data?.numbers || [];
  const broken = data?.broken || 0;
  if (nums.length === 0) return null;
  return (
    <section className={`rounded-lg border p-4 ${broken ? "border-amber-500/40 bg-amber-500/[0.05]" : "border-emerald-500/30 bg-emerald-500/[0.04]"}`} data-testid="number-health">
      <h2 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <GlobeHemisphereEast size={15} weight="bold" className="text-primary" /> Number health
        {broken > 0
          ? <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[9px] font-semibold text-amber-400">{broken} need attention</span>
          : <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] font-semibold text-emerald-400">All {nums.length} healthy</span>}
      </h2>
      <div className="grid gap-2 sm:grid-cols-2">
        {nums.map((n) => (
          <div key={n.number} data-testid={`num-health-${n.number}`} className="flex items-start gap-2 rounded-xl border border-white/10 bg-card px-3 py-2 text-sm">
            {n.healthy ? <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" /> : <Warning size={18} weight="fill" className="mt-0.5 shrink-0 text-amber-500" />}
            <div className="min-w-0">
              <p className="font-mono-metric font-semibold">{n.number}</p>
              <p className="text-xs text-muted-foreground">{n.reason}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function HunterHealth() {
  const [state, setState] = useState({ loading: true, data: null });
  const [loc, setLoc] = useState("");
  const [cat, setCat] = useState("restaurant");
  const run = React.useCallback(async (location, category) => {
    setState({ loading: true, data: null });
    try {
      const { data } = await api.post("/ai-hunter/self-test", {
        location: (location || "").trim() || undefined, category: category || "restaurant",
        require: "both", limit: 8 });
      setState({ loading: false, data });
    } catch (e) {
      setState({ loading: false, data: { ok: false, error: e?.response?.data?.detail || "Health check failed" } });
    }
  }, []);
  React.useEffect(() => { run("", "restaurant"); }, [run]);
  const d = state.data;
  const ok = d?.ok;
  const border = state.loading ? "border-white/10 bg-card"
    : ok ? "border-emerald-500/30 bg-emerald-500/[0.04]" : "border-amber-500/40 bg-amber-500/[0.05]";
  return (
    <section className={`rounded-lg border p-4 ${border}`} data-testid="hunter-health">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          <MagnifyingGlass size={15} weight="bold" className="text-primary" /> SI Hunter health
          {state.loading
            ? <span className="rounded-full bg-white/10 px-2 py-0.5 text-[9px] font-semibold text-muted-foreground">Checking…</span>
            : ok
              ? <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] font-semibold text-emerald-400" data-testid="hunter-health-ok">● Healthy</span>
              : <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[9px] font-semibold text-amber-400" data-testid="hunter-health-bad">● Needs attention</span>}
        </h2>
        <Link to="/app/ai-sales/hunter" className="text-xs font-semibold text-primary hover:underline" data-testid="hunter-health-open">Open Hunter →</Link>
      </div>
      <div className="mb-3 flex flex-col gap-2 sm:flex-row">
        <input data-testid="hunter-health-loc" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Test a market (e.g. Austin, Texas, USA)"
          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <input data-testid="hunter-health-cat" value={cat} onChange={(e) => setCat(e.target.value)} placeholder="restaurant"
          className="rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary sm:w-40" />
        <button data-testid="hunter-health-recheck" onClick={() => run(loc, cat)} disabled={state.loading}
          className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">{state.loading ? "Checking…" : "Run check"}</button>
        <button data-testid="hunter-autopilot-cc" disabled={state.loading}
          onClick={async () => {
            if (!loc.trim()) { toast.error("Enter a target market first"); return; }
            try {
              const { data } = await api.post("/ai-hunter/autopilot", { location: loc, category: cat });
              toast.success(data.message || "Autopilot ON — hands-free selling started");
            } catch (e) { toast.error(e?.response?.data?.detail || "Could not start Autopilot"); }
          }}
          className="rounded-full bg-gradient-to-r from-emerald-500 to-indigo-500 px-5 py-2 text-sm font-bold text-white disabled:opacity-60">🚀 Autopilot</button>
      </div>
      {state.loading ? (
        <p className="text-sm text-muted-foreground">Running a safe dry-run discovery (nothing imported)…</p>
      ) : ok ? (
        <p className="text-sm" data-testid="hunter-health-summary">
          <span className="font-semibold text-emerald-400">{d.meta.found} phone+email prospects</span> findable in {d.params.location} · {d.meta.candidates} scanned · {d.meta.enriched || 0} emails from websites · nothing imported
        </p>
      ) : (
        <p className="text-sm text-amber-500" data-testid="hunter-health-error">⚠️ {d?.error || "Discovery pipeline unavailable"}</p>
      )}
    </section>
  );
}

const SOCIAL_TZS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Sao_Paulo", "UTC", "Europe/London", "Europe/Paris", "Africa/Douala", "Africa/Lagos", "Asia/Dubai"];
function OtpHealthBadge() {
  const { data: h } = useQuery({ queryKey: ["otp-health"], queryFn: async () => (await api.get("/auth/otp-health")).data, refetchInterval: 60000 });
  if (!h) return null;
  const map = {
    ok: ["border-emerald-500/40 bg-emerald-500/15 text-emerald-400", "● Sign-up OTP healthy — SMS live"],
    degraded: ["border-amber-500/40 bg-amber-500/15 text-amber-400", "▲ SMS OTP down — email code fallback active"],
    down: ["border-destructive/40 bg-destructive/15 text-destructive", "■ Sign-up OTP DOWN — new customers can't verify"],
  };
  const [cls, label] = map[h.status] || map.down;
  return <div data-testid="otp-health-badge" className={`rounded-lg border px-4 py-2.5 text-sm font-semibold ${cls}`}>{label}</div>;
}

function AccountRecovery() {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!window.confirm("Activate everyone who started sign-up but couldn't finish while SMS was down? They'll log in with the password they already chose. Safe to run again.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/admin/recover-pending-signups?send_welcome=true");
      setRes(data);
      toast.success(`Activated ${data.activated} stuck sign-up(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Recovery failed"); }
    finally { setBusy(false); }
  };
  return (
    <section className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-4" data-testid="account-recovery">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <Users size={15} weight="bold" className="text-amber-500" /> Recover stuck sign-ups
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">Activate every customer who started sign-up but couldn't verify while SMS was down. They keep the password they chose and can log in immediately. Safe to run repeatedly.</p>
        </div>
        <button data-testid="recover-signups-btn" disabled={busy} onClick={run}
          className="shrink-0 rounded-full bg-amber-500 px-6 py-2.5 text-sm font-bold text-white disabled:opacity-60">
          {busy ? "Recovering…" : "Activate stuck sign-ups"}</button>
      </div>
      {res && (
        <div data-testid="recover-result" className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          <p>✓ Activated {res.activated} account(s){res.skipped_existing ? `, skipped ${res.skipped_existing} already registered` : ""}. They can log in now with their chosen password{res.activated ? " (and got a \u201cyour account is live\u201d email)" : ""}.</p>
          {res.activated_users?.length > 0 && (
            <div className="mt-2 max-h-56 overflow-auto rounded-lg border border-white/10">
              <table className="w-full text-left text-xs">
                <thead className="text-muted-foreground"><tr><th className="px-2 py-1">Email</th><th className="px-2 py-1">Phone</th><th className="px-2 py-1">Signed up</th></tr></thead>
                <tbody>
                  {res.activated_users.map((u, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-2 py-1 font-medium text-foreground">{u.email}</td>
                      <td className="px-2 py-1">{u.phone || "—"}</td>
                      <td className="px-2 py-1">{u.signed_up_at ? new Date(u.signed_up_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function PendingSignups() {
  const { data, refetch, isFetching } = useQuery({
    queryKey: ["signup-intents"],
    queryFn: async () => (await api.get("/admin/signup-intents")).data,
    refetchInterval: 60000,
  });
  const intents = data?.intents || [];
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const invite = async (it) => {
    setBusyId(it.id);
    try {
      await api.post(`/admin/signup-intents/${it.id}/invite`);
      toast.success(`Verification link sent to ${it.email}`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send invite"); }
    finally { setBusyId(null); }
  };

  const inviteAll = async () => {
    if (!window.confirm(`Email a self-verification link to all ${intents.length} unfinished sign-up(s)? Each person confirms themselves and their account goes live with the password they chose.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/signup-intents/invite-all");
      toast.success(`${data.sent} verification link(s) sent${data.skipped_existing ? `, ${data.skipped_existing} already active` : ""}${data.failed ? `, ${data.failed} failed` : ""}`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk invite failed"); }
    finally { setBulkBusy(false); }
  };

  return (
    <section className="rounded-lg border border-primary/30 bg-primary/[0.05] p-4" data-testid="pending-signups">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <Users size={15} weight="bold" className="text-primary" /> Pending sign-ups to approve
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-bold text-primary" data-testid="pending-count">{intents.length}</span>
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">Everyone who started sign-up but hasn't finished. Send each a secure link so they verify themselves — their account then goes live with the password they already chose. This list is durable and never auto-deletes.</p>
        </div>
        <button data-testid="invite-all-btn" disabled={bulkBusy || intents.length === 0} onClick={inviteAll}
          className="shrink-0 rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50">
          {bulkBusy ? "Sending…" : "Send verification link to all"}</button>
      </div>

      {intents.length === 0 ? (
        <p className="mt-3 rounded-xl border border-white/10 bg-white/5 px-3 py-4 text-center text-sm text-muted-foreground" data-testid="pending-empty">
          {isFetching ? "Loading…" : "No unfinished sign-ups right now. New ones will appear here automatically."}
        </p>
      ) : (
        <div className="mt-3 max-h-80 overflow-auto rounded-lg border border-white/10">
          <table className="w-full text-left text-xs">
            <thead className="sticky top-0 bg-card text-muted-foreground">
              <tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">Started</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Action</th></tr>
            </thead>
            <tbody>
              {intents.map((it) => (
                <tr key={it.id} data-testid={`intent-row-${it.id}`} className="border-t border-white/5">
                  <td className="px-3 py-2 font-medium text-foreground">{it.name || "—"}</td>
                  <td className="px-3 py-2">{it.email}</td>
                  <td className="px-3 py-2 font-mono-metric">{it.phone || "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{it.created_at ? new Date(it.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${it.status === "invited" ? "bg-sky-500/15 text-sky-400" : "bg-amber-500/15 text-amber-400"}`}>
                      {it.status === "invited" ? "Invited" : "Pending"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`invite-${it.id}`} disabled={busyId === it.id} onClick={() => invite(it)}
                      className="rounded-full border border-primary/40 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-50">
                      {busyId === it.id ? "Sending…" : it.status === "invited" ? "Resend link" : "Send link"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function FullAutopilot() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [includeSms, setIncludeSms] = useState(true);
  const [huntBusy, setHuntBusy] = useState(false);
  const [huntResult, setHuntResult] = useState(null);
  const huntMax = async () => {
    if (!window.confirm("This will start hunting NEW businesses across 10 US markets every day and auto-email each qualified prospect a Cloud PBX pitch. Continue?")) return;
    setHuntBusy(true);
    try {
      const { data } = await api.post("/ai-hunter/autopilot-max");
      setHuntResult(data);
      toast.success(`Max hunter ON — ${data.markets} US markets, daily`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start max hunter"); }
    finally { setHuntBusy(false); }
  };
  const launchBlast = async () => {
    if (!window.confirm("This will turn ON every product's auto-sell, grant marketing consent to all active members, and send a campaign to EVERYONE right now. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai-outreach/launch-blast", { include_sms: includeSms });
      setResult(data);
      toast.success(`Blast launched to ${data.members} members — selling on full autopilot`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not launch blast"); }
    finally { setBusy(false); }
  };
  return (
    <section className="rounded-lg border border-indigo-500/30 bg-indigo-500/[0.05] p-4" data-testid="full-autopilot">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            <Rocket size={15} weight="bold" className="text-primary" /> Full autopilot — sell everything now
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">Turns on every product's auto-sell (onboarding, cross-sell, win-back, lead first-touch, checkout rescue, PBX trial), opts every active member into marketing, and <strong>sends a campaign to everyone immediately</strong>. Each send still respects opt-outs &amp; quiet hours.</p>
          <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground" data-testid="autopilot-sms-toggle">
            <input type="checkbox" checked={includeSms} onChange={(e) => setIncludeSms(e.target.checked)} className="h-4 w-4 accent-emerald-500" />
            Also text members with a phone number (needs Telnyx LIVE + A2P 10DLC to deliver)
          </label>
        </div>
        <button data-testid="autopilot-enable-all" disabled={busy} onClick={launchBlast}
          className="shrink-0 rounded-full bg-gradient-to-r from-emerald-500 to-indigo-500 px-6 py-2.5 text-sm font-bold text-white disabled:opacity-60">
          {busy ? "Launching…" : "🚀 Turn on & blast everyone"}</button>
      </div>
      {result && (
        <div data-testid="autopilot-result" className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          ✓ Campaign launched to <strong>{result.members}</strong> members ({result.with_phone} with a phone) · channels live: <strong>{(result.channels_on || []).join(", ")}</strong>. {result.note}
        </div>
      )}
      <div className="mt-4 border-t border-white/10 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Hunt NEW businesses daily (all markets)</p>
            <p className="mt-1 text-sm text-muted-foreground">Auto-discovers call-heavy US small businesses across 10 major markets (dental, legal, real estate, auto, clinics, salons, vet, insurance) every day and auto-emails each qualified phone+email prospect a Cloud PBX + SI Receptionist pitch. This is your $5k engine.</p>
          </div>
          <button data-testid="autopilot-max-hunt" disabled={huntBusy} onClick={huntMax}
            className="shrink-0 rounded-full border-2 border-emerald-500 px-6 py-2.5 text-sm font-bold text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-60">
            {huntBusy ? "Starting…" : "🌎 Start max hunter"}</button>
        </div>
        {huntResult && (
          <div data-testid="autopilot-max-result" className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-300">
            ✓ Hunting <strong>{huntResult.markets}</strong> US markets daily (up to {huntResult.lead_cap_day} new leads/day): {(huntResult.plan || []).map((p) => `${p.category} · ${p.location.split(",")[0]}`).join("  ·  ")}
          </div>
        )}
      </div>
    </section>
  );
}

function SocialAutopost() {
  const [cfg, setCfg] = useState({ page_id: "", has_token: false, enabled: false, configured: false });
  const [pageId, setPageId] = useState("");
  const [token, setToken] = useState("");
  const [schedule, setSchedule] = useState({ timezone: "America/New_York", slots: [] });
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState(null);
  const loadCfg = React.useCallback(async () => {
    try {
      const { data } = await api.get("/ai-outreach/social-config");
      setCfg(data); setPageId(data.page_id || "");
      if (data.schedule) setSchedule(data.schedule);
    } catch (e) { /* ignore */ }
  }, []);
  React.useEffect(() => { loadCfg(); }, [loadCfg]);
  const save = async (enabled) => {
    setBusy(true);
    try {
      const body = { page_id: pageId, schedule };
      if (token) body.page_access_token = token;
      if (enabled !== undefined) body.enabled = enabled;
      await api.post("/ai-outreach/social-config", body);
      toast.success(enabled === false ? "Posting paused" : "Saved — posting on your schedule");
      setToken(""); await loadCfg();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const postNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai-outreach/social-test");
      if (data.ok) toast.success("Posted to your Facebook Page ✓");
      else toast.error(data.result?.error || data.result?.reason || "Post failed");
    } catch (e) { toast.error("Post failed"); } finally { setBusy(false); }
  };
  const validate = async () => {
    setBusy(true); setValidation(null);
    try {
      const { data } = await api.post("/ai-outreach/social-validate");
      setValidation(data);
      if (data.valid) toast.success(`Token valid — connected to ${data.page_name || "your Page"} ✓`);
      else toast.error(data.error || "Token invalid");
    } catch (e) {
      setValidation({ valid: false, error: "Request failed" });
      toast.error("Validation failed");
    } finally { setBusy(false); }
  };
  const setSlot = (i, patch) => setSchedule((s) => ({ ...s, slots: s.slots.map((sl, j) => j === i ? { ...sl, ...patch } : sl) }));
  const addSlot = () => setSchedule((s) => ({ ...s, slots: [...s.slots, { id: `s${Date.now()}`, time: "09:00", enabled: true, label: "" }] }));
  const removeSlot = (i) => setSchedule((s) => ({ ...s, slots: s.slots.filter((_, j) => j !== i) }));
  const enabledCount = schedule.slots.filter((s) => s.enabled).length;
  return (
    <section className="rounded-lg border border-white/10 bg-card p-4" data-testid="social-autopost">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          <Broadcast size={15} weight="bold" className="text-primary" /> Facebook auto-post
          <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${cfg.configured ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-muted-foreground"}`}>
            {cfg.configured ? (cfg.enabled ? `● Live · ${enabledCount}×/day` : "Configured") : "Not connected"}</span>
        </h2>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input data-testid="fb-page-id" value={pageId} onChange={(e) => setPageId(e.target.value)} placeholder="Facebook Page ID"
          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <input data-testid="fb-token" type="password" value={token} onChange={(e) => setToken(e.target.value)}
          placeholder={cfg.has_token ? "•••••• saved (leave blank to keep)" : "Page access token"}
          className="min-w-0 flex-1 rounded-xl border border-white/10 bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
      </div>

      <div className="mt-4 rounded-xl border border-white/10 bg-background/50 p-3" data-testid="fb-schedule">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Posting schedule</p>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">Timezone
            <select data-testid="fb-tz" value={schedule.timezone} onChange={(e) => setSchedule((s) => ({ ...s, timezone: e.target.value }))}
              className="rounded-lg border border-white/10 bg-background px-2 py-1 text-xs outline-none focus:border-primary">
              {SOCIAL_TZS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>
        <div className="space-y-2">
          {schedule.slots.map((sl, i) => (
            <div key={sl.id || i} className="flex flex-wrap items-center gap-2" data-testid={`fb-slot-${i}`}>
              <button type="button" data-testid={`fb-slot-enabled-${i}`} onClick={() => setSlot(i, { enabled: !sl.enabled })}
                className={`h-6 w-11 shrink-0 rounded-full transition-colors ${sl.enabled ? "bg-primary" : "bg-white/15"}`}>
                <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${sl.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
              <input type="time" data-testid={`fb-slot-time-${i}`} value={sl.time} onChange={(e) => setSlot(i, { time: e.target.value })}
                className="rounded-lg border border-white/10 bg-background px-2 py-1 text-sm outline-none focus:border-primary" />
              <input data-testid={`fb-slot-label-${i}`} value={sl.label || ""} onChange={(e) => setSlot(i, { label: e.target.value })} placeholder="Label (optional)"
                className="min-w-0 flex-1 rounded-lg border border-white/10 bg-background px-2 py-1 text-xs outline-none focus:border-primary" />
              <button type="button" data-testid={`fb-slot-remove-${i}`} onClick={() => removeSlot(i)}
                className="shrink-0 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:text-destructive">Remove</button>
            </div>
          ))}
          {schedule.slots.length === 0 && <p className="text-xs text-muted-foreground">No times yet — add one below.</p>}
        </div>
        <button type="button" data-testid="fb-add-slot" onClick={addSlot}
          className="mt-2 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground">+ Add posting time</button>
        <p className="mt-2 text-[11px] text-muted-foreground">DST-aware — times follow the timezone above. Each enabled time posts once/day, rotating products &amp; landing pages so posts never repeat.</p>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <button data-testid="fb-save" disabled={busy} onClick={() => save(true)}
          className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">Save &amp; enable</button>
        <button data-testid="fb-post-now" disabled={busy || !cfg.configured} onClick={postNow}
          className="rounded-full border border-emerald-500/40 px-5 py-2 text-sm font-semibold text-emerald-600 disabled:opacity-60">Post now (test)</button>
        <button data-testid="fb-validate" disabled={busy || !cfg.configured} onClick={validate}
          className="rounded-full border border-primary/40 px-5 py-2 text-sm font-semibold text-primary disabled:opacity-60">Validate token</button>
        {cfg.enabled && <button data-testid="fb-disable" disabled={busy} onClick={() => save(false)}
          className="rounded-full border border-border px-4 py-2 text-sm text-muted-foreground">Pause all</button>}
      </div>
      {validation && (
        <div data-testid="fb-validation-result"
          className={`mt-3 rounded-xl border p-3 text-sm ${validation.valid ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-destructive/40 bg-destructive/10 text-destructive"}`}>
          {validation.valid ? (
            <>✓ Connected to <strong>{validation.page_name || "your Page"}</strong>{validation.page_id ? ` (ID ${validation.page_id})` : ""}.{validation.can_post ? " This token can publish posts." : " ⚠ This token may lack posting permission (needs CREATE_CONTENT/MANAGE)."}</>
          ) : (
            <>✕ {validation.error}{validation.code ? ` (code ${validation.code}${validation.subcode ? `/${validation.subcode}` : ""})` : ""}. Refresh your long-lived Page token from Meta and re-save.</>
          )}
        </div>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">Token is stored securely server-side, never shown in the browser. Your existing connection is untouched.</p>
    </section>
  );
}

const KIND_LABEL = { pbx_subscribe: "PBX + SI Receptionist", did_purchase: "Global Numbers", wallet_topup: "Wallet Top-ups", esim_purchase: "eSIM", other: "Other" };
const TAP_LABEL = { esim: "eSIM", business: "PBX/Receptionist", numbers: "Numbers", meet: "Meetings", calls: "Calls" };
function MonthRevenue() {
  const { data } = useQuery({ queryKey: ["ai-revenue-snapshot"], queryFn: async () => (await api.get("/ai-sales/revenue-snapshot")).data, refetchInterval: 30000 });
  if (!data) return null;
  const rev = data.month_revenue_cents || 0;
  const target = data.target_cents || 500000;
  const pct = Math.min(100, Math.round((rev / target) * 100));
  const kinds = Object.entries(data.by_kind || {}).sort((a, b) => b[1].amount_cents - a[1].amount_cents);
  return (
    <section className="space-y-3" data-testid="month-revenue">
      <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <TrendUp size={15} weight="bold" className="text-primary" /> This month — toward your ${(target / 100).toLocaleString()} goal
      </h2>
      <div className="rounded-lg border border-primary/25 bg-primary/[0.05] p-4">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <p className="font-mono-metric text-3xl font-semibold text-primary" data-testid="month-revenue-value">{money(rev)}</p>
          <p className="text-xs text-muted-foreground">{pct}% of goal · {data.orders || 0} orders</p>
        </div>
        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} data-testid="month-revenue-bar" />
        </div>
        {kinds.length > 0 && (
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {kinds.map(([k, v]) => (
              <div key={k} className="rounded-xl border border-white/10 bg-card px-3 py-2" data-testid={`rev-kind-${k}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{KIND_LABEL[k] || k}</p>
                <p className="font-mono-metric text-sm font-semibold">{money(v.amount_cents)} <span className="text-xs text-muted-foreground">· {v.count}</span></p>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 font-semibold text-emerald-400" data-testid="active-pbx">{data.active_pbx_subscriptions || 0} active PBX subs</span>
          <span className="rounded-full bg-amber-500/10 px-2.5 py-1 font-semibold text-amber-400" data-testid="recoverable">{money(data.recoverable_pending_cents || 0)} recoverable · {data.recoverable_pending_count || 0} unfinished checkouts</span>
          <span className="rounded-full bg-white/5 px-2.5 py-1 font-semibold text-muted-foreground">{data.leads_in_pipeline || 0} leads in pipeline</span>
        </div>
        {Object.keys(data.taps_by_slug || {}).length > 0 && (
          <div className="mt-3 border-t border-white/10 pt-3" data-testid="tap-funnel">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Link taps this month — demand by intent</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.taps_by_slug).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <span key={k} data-testid={`tap-${k}`} className="rounded-full border border-white/10 px-2.5 py-1 text-xs"><b className="text-primary">{v}</b> {TAP_LABEL[k] || k}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function SignupFunnel() {
  const { data } = useQuery({ queryKey: ["signup-analytics"], queryFn: async () => (await api.get("/admin/signup-analytics")).data, refetchInterval: 60000 });
  if (!data) return null;
  const started = data.started || 0, finished = data.finished || 0, pending = data.pending || 0;
  return (
    <section className="space-y-3" data-testid="signup-funnel">
      <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <ChartLineUp size={15} weight="bold" className="text-primary" /> Sign-up funnel — started vs finished
      </h2>
      <div className="rounded-lg border border-white/10 bg-card p-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Metric label="Started sign-up" value={started} sub="Began registration" />
          <Metric label="Finished" value={finished} sub={`${data.finished_pct || 0}% completed`} accent="text-emerald-400" />
          <Metric label="Not finished" value={pending} sub={`${data.drop_off_pct || 0}% drop-off`} accent={pending > 0 ? "text-yellow-400" : ""} />
        </div>
        <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${data.finished_pct || 0}%` }} data-testid="funnel-bar" />
        </div>
        {pending > 0 && (
          <p className="mt-3 text-xs text-yellow-300/90" data-testid="funnel-dropoff">
            {pending} {pending === 1 ? "person" : "people"} started but didn't finish — <Link to="/command/users" className="font-semibold underline">activate them in Customers →</Link>
          </p>
        )}
      </div>
    </section>
  );
}

function GoogleCalendarConnect() {
  const { data, refetch } = useQuery({ queryKey: ["google-status"], queryFn: async () => (await api.get("/admin/google/status")).data, refetchInterval: 20000 });
  const [cid, setCid] = useState("");
  const [sec, setSec] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("google");
    if (p === "connected") { toast.success("Google Calendar connected"); refetch(); }
    else if (p === "error") { toast.error("Google connection failed — check credentials & redirect URI"); }
    if (p) window.history.replaceState({}, "", "/command");
  }, []); // eslint-disable-line

  const saveCreds = async () => {
    if (!cid.trim() || !sec.trim()) { toast.error("Enter both Client ID and Secret"); return; }
    setBusy(true);
    try { await api.post("/admin/google/credentials", { client_id: cid, client_secret: sec }); toast.success("Saved — now click Connect"); setCid(""); setSec(""); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const connect = async () => {
    try { const { data } = await api.get("/admin/google/connect"); window.location.href = data.authorization_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start Google authorization"); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar?")) return;
    try { await api.delete("/admin/google/disconnect"); toast.success("Disconnected"); refetch(); }
    catch (e) { toast.error("Failed"); }
  };
  if (!data) return null;

  return (
    <section className="space-y-3" data-testid="google-calendar-connect">
      <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        <CheckCircle size={15} weight="bold" className="text-primary" /> Google Calendar
        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${data.connected ? "bg-emerald-500/15 text-emerald-400" : data.configured ? "bg-amber-500/15 text-amber-400" : "bg-white/10 text-muted-foreground"}`}>
          {data.connected ? "CONNECTED" : data.configured ? "KEY SET · NOT CONNECTED" : "NOT CONFIGURED"}
        </span>
      </h2>
      <div className="rounded-lg border border-white/10 bg-card p-4">
        {data.connected ? (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-emerald-400">✓ Connected as <b>{data.calendar_email}</b> — bookings sync to this calendar in real time.</p>
            <button data-testid="google-disconnect" onClick={disconnect} className="rounded-full border border-red-500/40 px-3 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-500/10">Disconnect</button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Paste your Google OAuth credentials, then connect the iYeneh calendar. Add this exact redirect URI in Google Cloud Console:</p>
            <code className="block break-all rounded-lg bg-background px-3 py-2 text-[11px] text-primary" data-testid="google-redirect-uri">{data.redirect_uri}</code>
            <div className="grid gap-2 sm:grid-cols-2">
              <input data-testid="google-client-id" value={cid} onChange={(e) => setCid(e.target.value)} placeholder={data.configured ? "Client ID (set — leave to keep)" : "GOOGLE_CLIENT_ID"} className="rounded-lg border border-white/10 bg-background px-3 py-2 font-mono-metric text-xs outline-none focus:border-primary" />
              <input data-testid="google-client-secret" type="password" value={sec} onChange={(e) => setSec(e.target.value)} placeholder={data.configured ? "Client Secret (set — leave to keep)" : "GOOGLE_CLIENT_SECRET"} className="rounded-lg border border-white/10 bg-background px-3 py-2 font-mono-metric text-xs outline-none focus:border-primary" />
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="google-save-creds" onClick={saveCreds} disabled={busy} className="rounded-full bg-white/10 px-4 py-2 text-xs font-semibold hover:bg-white/20 disabled:opacity-50">{busy ? "Saving…" : "Save credentials"}</button>
              <button data-testid="google-connect" onClick={connect} disabled={!data.configured} className="rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground disabled:opacity-40">Connect Google Calendar</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default function CommandCenter() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-dashboard"], queryFn: async () => (await api.get("/admin/dashboard")).data, refetchInterval: 15000 });
  const { data: earn } = useQuery({ queryKey: ["admin-earnings-today"], queryFn: async () => (await api.get("/admin/earnings-today")).data, refetchInterval: 15000 });
  const { data: push } = useQuery({ queryKey: ["push-stats"], queryFn: async () => (await api.get("/admin/push/stats")).data, refetchInterval: 30000 });

  if (isLoading || !data) return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  const f = data.financial, c = data.customers, t = data.telecom, r = data.routing, d = data.digital, b = data.broadcast, k = data.risk;
  const chart = (data.by_product || []).map((p) => ({ name: p._id, revenue: p.revenue / 100, profit: p.profit / 100 }));
  const et = earn?.today;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">CEO Command Center</h1>
          <p className="mt-1 text-sm text-muted-foreground">Live platform intelligence</p>
        </div>
        <span className="flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-400">
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" /> {k.system_health}
        </span>
      </div>

      {/* TODAY earnings — live money strip */}
      <section className="space-y-3" data-testid="earnings-today">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          <CurrencyDollar size={15} weight="bold" className="text-emerald-400" /> Today's earnings
          <span className="ml-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[9px] font-semibold text-emerald-400">LIVE</span>
        </h2>
        <div className="grid gap-3 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="Revenue Today" value={money(et?.revenue_cents || 0)} accent="text-emerald-400" data-testid="today-revenue" />
          <Metric label="Margin Today" value={money(et?.profit_cents || 0)} sub={`${et?.margin_pct || 0}% margin`} accent="text-primary" />
          <Metric label="Sales Today" value={et?.sales ?? 0} sub={earn ? `${earn.month.sales} this month` : ""} />
          <Metric label="Card Funds In" value={money(et?.funded_cents || 0)} sub="Wallet top-ups paid today" />
        </div>
      </section>

      <MonthRevenue />

      <QuickCredit />

      <NumberHealth />

      <HunterHealth />

      <OtpHealthBadge />

      <FullAutopilot />

      <AccountRecovery />
      <PendingSignups />
      <SignupFunnel />
      <GoogleCalendarConnect />

      <SocialAutopost />

      {/* headline finance */}
      <div className="trace-border grid gap-3 rounded-lg border border-white/10 bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Revenue" value={money(f.revenue_cents)} sub="All-time (MRR proxy)" accent="text-primary" />
        <Metric label="Gross Profit" value={money(f.gross_profit_cents)} sub={`Margin ${f.margin_pct}%`} accent="text-emerald-400" />
        <Metric label="Provider Cost" value={money(f.provider_cost_cents)} sub="Wholesale spend" />
        <Metric label="LCR Savings" value={money(f.lcr_savings_cents)} sub="vs. naive routing" accent="text-primary" />
      </div>

      <Section title="Financial" icon={CurrencyDollar}>
        <Metric label="MRR" value={money(f.mrr_cents)} />
        <Metric label="ARR" value={money(f.arr_cents)} />
        <Metric label="Outstanding Balances" value={money(f.outstanding_balance_cents)} sub="Customer wallets" />
        <Metric label="Margin %" value={`${f.margin_pct}%`} accent="text-emerald-400" />
      </Section>

      <Section title="Customers" icon={Users}>
        <Metric label="Customers" value={c.total} />
        <Metric label="New Today" value={c.new_today} />
        <Metric label="Resellers" value={c.resellers} />
        <Metric label="Countries" value={c.countries} />
      </Section>

      <Section title="Call Notifications Reach" icon={BellRinging}>
        <Metric label="Coverage" value={`${push?.coverage_pct ?? 0}%`} accent="text-primary" />
        <Metric label="Members Enabled" value={push?.subscribed_users ?? 0} sub={`of ${push?.total_members ?? 0} members`} />
        <Metric label="Devices Subscribed" value={push?.total_subscriptions ?? 0} sub="Push endpoints" />
        <Metric label="Closed-App Ring" value={(push?.subscribed_users ?? 0) > 0 ? "Ready" : "None yet"} accent={(push?.subscribed_users ?? 0) > 0 ? "text-emerald-400" : ""} />
      </Section>

      <div className="grid gap-8 lg:grid-cols-2">
        <Section title="Telecom" icon={GlobeHemisphereEast}>
          <Metric label="DIDs" value={t.dids} />
          <Metric label="Active Calls" value={t.active_calls} />
          <Metric label="Minutes" value={t.minutes} />
          <Metric label="Failed Calls" value={t.failed_calls} />
        </Section>
        <Section title="Routing (LCR)" icon={Path}>
          <Metric label="Carrier Spend" value={money(r.carrier_spend_cents)} />
          <Metric label="Route Quality" value={`${r.route_quality}%`} accent="text-emerald-400" />
          <Metric label="Failover" value={r.failover_events} />
          <Metric label="Mode" value={r.mode} accent="text-primary" />
        </Section>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <Section title="Digital Services" icon={TrendUp}>
          <Metric label="Top Ups" value={d.topups} />
          <Metric label="eSIMs" value={d.esims} />
          <Metric label="Digital Revenue" value={money(d.revenue_cents)} accent="text-primary" />
          <Metric label="—" value="" />
        </Section>
        <Section title="Broadcast" icon={Broadcast}>
          <Metric label="Campaigns" value={b.campaigns} />
          <Metric label="Recipients" value={b.recipients} />
          <Metric label="Delivered" value={b.delivered} accent="text-emerald-400" />
          <Metric label="—" value="" />
        </Section>
      </div>

      {/* revenue by product */}
      <section className="space-y-3">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground"><ChartLineUp size={15} weight="bold" className="text-primary" /> Revenue by product</h2>
        <div className="rounded-lg border border-white/10 bg-card p-4">
          {chart.length === 0 ? <p className="py-10 text-center text-sm text-muted-foreground">No revenue yet</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chart}>
                <XAxis dataKey="name" tick={{ fill: "#8a90a2", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }}
                  contentStyle={{ background: "#08111F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }}
                  formatter={(v) => `$${v.toFixed(2)}`} />
                <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                  {chart.map((_, i) => <Cell key={i} fill="#1D4ED8" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* risk + recent */}
      <div className="grid gap-8 lg:grid-cols-2">
        <Section title="Risk" icon={ShieldWarning}>
          <Metric label="Fraud Alerts" value={k.fraud_alerts} accent={k.fraud_alerts ? "text-destructive" : ""} />
          <Metric label="Failed Payments" value={k.failed_payments} />
          <Metric label="KYC Issues" value={k.kyc_issues} />
          <Metric label="Provider Problems" value={k.provider_problems} />
        </Section>
        <section className="space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Recent transactions</h2>
          <div className="rounded-lg border border-white/10 bg-card">
            {(data.recent_transactions || []).map((tx, i) => (
              <div key={i} className="flex items-center justify-between border-b border-white/5 px-4 py-2.5 text-sm last:border-0">
                <span className="truncate capitalize text-muted-foreground">{tx.product?.replace(/_/g, " ")}</span>
                <span className="font-mono-metric font-semibold">{tx.direction === "credit" ? "+" : "−"}{money(tx.amount_cents)}</span>
              </div>
            ))}
            {(!data.recent_transactions || data.recent_transactions.length === 0) && <p className="px-4 py-8 text-center text-sm text-muted-foreground">No transactions</p>}
          </div>
        </section>
      </div>
    </div>
  );
}
